
import Vue from 'vue';
import Component from 'vue-class-component';

type SupportedOs = 'windows' | 'mac' | 'linux';

interface UsageStep {
  key: string;
  text: string;
  icon: string;
  imageSrc?: string;
  alt?: string;
}

interface OsOption {
  key: SupportedOs;
  label: string;
  icon: string;
}

@Component({})
export default class UsageGuideCard extends Vue {
  usageOpen = false;
  selectedOs: SupportedOs = 'windows';
  systemOs: SupportedOs = 'windows';

  readonly osOptions: OsOption[] = [
    { key: 'windows', label: 'Windows', icon: 'mdi-microsoft-windows' },
    { key: 'mac', label: 'macOS', icon: 'mdi-apple' },
    { key: 'linux', label: 'Linux', icon: 'mdi-linux' },
  ];

  readonly usageGuideByOs: Record<SupportedOs, UsageStep[]> = {
    windows: [
      {
        key: 'autoStart',
        text: 'Uygulama ilk çalıştırıldığında imza servisi arka planda otomatik olarak başlatılır.',
        icon: 'mdi-rocket-launch-outline',
      },
      {
        key: 'trayIcon',
        text: 'Servis çalışırken Windows görev çubuğunun sağ alt kısmında Zirve simgesini görürsünüz.',
        icon: 'mdi-monitor-eye',
        imageSrc: 'kk1.png',
        alt: 'Windows bildirim alanı simgesi',
      },
      {
        key: 'kart',
        text: 'E-imza kartınızı taktığınızda UYAP oturumu kendiliğinden açılır; PIN yalnızca ilk seferde sorulur.',
        icon: 'mdi-smart-card-outline',
      },
      {
        key: 'restart',
        text: 'Servis beklenmedik şekilde durursa otomatik olarak yeniden başlatılır. Sorun sürerse uygulamayı kapatıp açın.',
        icon: 'mdi-reload',
      },
    ],
    mac: [
      {
        key: 'autoStart',
        text: 'Uygulama açıldığında servis otomatik olarak çalışır ve menü çubuğunun sağ üstünde simge görünür.',
        icon: 'mdi-rocket-launch-outline',
      },
      {
        key: 'trayIcon',
        text: 'Simge üzerinden servis durumunu gözlemleyebilir ve bağlantıyı kontrol edebilirsiniz.',
        icon: 'mdi-eye-circle-outline',
        imageSrc: 'mac.png',
        alt: 'macOS menü çubuğu simgesi',
      },
      {
        key: 'kart',
        text: 'E-imza kartınızı taktığınızda UYAP oturumu kendiliğinden açılır; PIN yalnızca ilk seferde sorulur.',
        icon: 'mdi-smart-card-outline',
      },
      {
        key: 'restart',
        text: 'Serviste gecikme ya da hata olursa servis kendiliğinden toparlanır. Sorun sürerse uygulamayı kapatıp açın.',
        icon: 'mdi-restore-alert',
      },
    ],
    linux: [
      {
        key: 'autoStart',
        text: 'Servis, uygulama açıldığında sistem tepsisinde arka planda otomatik başlar.',
        icon: 'mdi-rocket-launch-outline',
      },
      {
        key: 'trayIcon',
        text: 'Çalışma durumunu masaüstü ortamınızın bildirim alanındaki Zirve simgesi üzerinden takip edin.',
        icon: 'mdi-lan-connect',
      },
      {
        key: 'kart',
        text: 'E-imza kartınızı taktığınızda UYAP oturumu kendiliğinden açılır; PIN yalnızca ilk seferde sorulur.',
        icon: 'mdi-smart-card-outline',
      },
      {
        key: 'restart',
        text: 'Bağlantı sorunlarında servis süreçleri kendiliğinden tazelenir. Sorun sürerse uygulamayı kapatıp açın.',
        icon: 'mdi-update',
      },
    ],
  };

  mounted() {
    const detectedOs = this.detectOs();
    this.systemOs = detectedOs;
    this.selectedOs = detectedOs;

    this.restoreUsageState();

    this.$watch('usageOpen', (val: boolean) => {
      try {
        localStorage.setItem('home.usageOpen', String(val));
      } catch (_) {
        /* ignore */
      }
    });
  }

  get usageSteps(): UsageStep[] {
    return this.usageGuideByOs[this.selectedOs];
  }

  get systemOsOption(): OsOption | undefined {
    return this.osOptions.find(o => o.key === this.systemOs);
  }

  get osTabIndex(): number {
    return Math.max(0, this.osOptions.findIndex(o => o.key === this.selectedOs));
  }

  set osTabIndex(index: number) {
    const option = this.osOptions[index];
    if (option) {
      this.selectedOs = option.key;
    }
  }

  private detectOs(): SupportedOs {
    if (typeof navigator === 'undefined') {
      return 'windows';
    }

    const platform = navigator?.platform?.toLowerCase() ?? '';
    const ua = navigator?.userAgent?.toLowerCase() ?? '';

    if (platform.includes('mac') || ua.includes('mac os')) {
      return 'mac';
    }

    if (platform.includes('linux') || ua.includes('linux')) {
      return 'linux';
    }

    return 'windows';
  }

  private restoreUsageState() {
    if (typeof localStorage === 'undefined') return;
    try {
      const savedUsage = localStorage.getItem('home.usageOpen');
      if (savedUsage === 'true' || savedUsage === 'false') {
        this.usageOpen = savedUsage === 'true';
      }
    } catch (_) {
      /* ignore */
    }
  }
}
